import React, { useState, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { ChevronLeft, ChevronRight, X, Eye } from 'lucide-react';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

// Use same worker configuration as SigningSession
pdfjs.GlobalWorkerOptions.workerSrc = `${window.location.origin}/pdf.worker.min.mjs`;
console.log('DocumentPreview - PDF.js Worker configured:', pdfjs.GlobalWorkerOptions.workerSrc);

const DocumentPreview = ({ pdfUrl, fields = [], onClose, title = "Document Preview" }) => {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageWidth, setPageWidth] = useState(0);
  const containerRef = React.useRef(null);

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        const width = containerRef.current.offsetWidth;
        setPageWidth(width - 40); // Subtract padding
      }
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  const goToPrevPage = () => {
    setPageNumber((prev) => Math.max(prev - 1, 1));
  };

  const goToNextPage = () => {
    setPageNumber((prev) => Math.min(prev + 1, numPages));
  };

  const getFieldColor = (fieldType) => {
    const colors = {
      signature: '#DDD6FE',
      initials: '#FCE7F3',
      text: '#DBEAFE',
      email: '#D1FAE5',
      date: '#FED7AA',
      checkbox: '#E9D5FF',
      name: '#BAE6FD'
    };
    return colors[fieldType] || '#E5E7EB';
  };

  const getFieldLabel = (field) => {
    if (field.label) return field.label;
    return field.type?.charAt(0).toUpperCase() + field.type?.slice(1) || 'Field';
  };

  // Filter fields for current page
  const currentPageFields = fields.filter(field => {
    // Handle both field.page and field.rect.page, default to page 1
    const fieldPage = field.page || (field.rect && field.rect.page) || 1;
    return fieldPage === pageNumber;
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <Card className="w-full max-w-6xl h-[90vh] flex flex-col bg-white">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <Eye className="text-purple-600" size={24} />
            <h2 className="text-xl font-bold text-gray-900">{title}</h2>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={20} />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto">
          <div className="grid md:grid-cols-[1fr_300px] gap-4 p-4 h-full">
            {/* PDF Viewer */}
            <div className="flex flex-col">
              <div
                ref={containerRef}
                className="relative bg-gray-100 rounded-lg overflow-auto flex-1 flex items-center justify-center"
              >
                <div className="relative">
                  <Document
                    file={pdfUrl}
                    onLoadSuccess={onDocumentLoadSuccess}
                    loading={
                      <div className="flex items-center justify-center p-8">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
                      </div>
                    }
                  >
                    <Page
                      pageNumber={pageNumber}
                      width={pageWidth}
                      renderTextLayer={true}
                      renderAnnotationLayer={true}
                    />
                    
                    {/* Field Overlays - positioned relative to Page */}
                    <div className="absolute inset-0 pointer-events-none">
                      {currentPageFields.map((field, index) => {
                        // Get field coordinates - handle both rect object and direct properties
                        const rect = field.rect || {};
                        let x = rect.x !== undefined ? rect.x : field.x || 0;
                        let y = rect.y !== undefined ? rect.y : field.y || 0;
                        let w = rect.w !== undefined ? rect.w : field.width || 0;
                        let h = rect.h !== undefined ? rect.h : field.height || 0;

                        console.log('Preview Field:', { 
                          id: field.id, 
                          type: field.type,
                          originalCoords: { x, y, w, h },
                          page: field.page
                        });

                        // Check if coordinates are in pixels (>1) and convert to ratios
                        if (x > 1 || y > 1 || w > 1 || h > 1) {
                          // These are pixel coordinates from DocumentFieldEditor
                          // The y-coordinate is absolute (includes previous pages)
                          // Need to make it relative to current page
                          const pageHeight = 1300; // Height per page in DocumentFieldEditor
                          const pageWidth = 612; // Standard PDF width in points
                          
                          // Calculate page-relative y-coordinate
                          // If field is on page 2, subtract height of page 1
                          const pageRelativeY = y - (pageHeight * (field.page - 1));
                          
                          console.log('Converting from pixels:', { 
                            absoluteY: y, 
                            page: field.page, 
                            pageRelativeY,
                            pageHeight 
                          });
                          
                          // Convert to ratios based on standard PDF dimensions
                          x = x / pageWidth;
                          y = pageRelativeY / pageHeight;
                          w = w / pageWidth;
                          h = h / pageHeight;
                          
                          console.log('Converted to ratios:', { x, y, w, h });
                        }

                        const leftPercent = x * 100;
                        const topPercent = y * 100;
                        const widthPercent = w * 100;
                        const heightPercent = h * 100;

                        console.log('Final positioning:', { 
                          left: `${leftPercent}%`,
                          top: `${topPercent}%`,
                          width: `${widthPercent}%`,
                          height: `${heightPercent}%`
                        });

                        // Render radio/checkbox groups with individual circles/squares ONLY (no labels)
                        if (field.type === 'radio' || field.type === 'checkbox-group') {
                          const options = field.props?.options || [];

                          // Render each circle separately, positioned on the page
                          return (
                            <React.Fragment key={field.id || index}>
                              {options.length > 0 ? (
                                options.map((option, optIdx) => {
                                  // Use exact same coordinate calculation as editor (FieldNodeV2)
                                  const pageW = pageMetrics.width;
                                  const pageH = pageMetrics.height;

                                  const fieldX = field.rect.x * pageW;
                                  const fieldY = field.rect.y * pageH;
                                  const fieldW = field.rect.w * pageW;
                                  const fieldH = field.rect.h * pageH;

                                  // Option x,y are ratios (0-1) within the field container
                                  // These represent the TOP-LEFT corner of the circle (matching editor behavior)
                                  // Editor draws: cx = optionX + radioSize/2, so optionX is top-left
                                  const optX = (option.x !== undefined ? option.x : 0) * fieldW;
                                  const optY = (option.y !== undefined ? option.y : 0) * fieldH;

                                  // Calculate absolute position on page (this is the TOP-LEFT position)
                                  const absoluteX = fieldX + optX;
                                  const absoluteY = fieldY + optY;

                                  // Convert back to percentage for CSS
                                  const circleLeftPercent = (absoluteX / pageW) * 100;
                                  const circleTopPercent = (absoluteY / pageH) * 100;

                                  return (
                                    <div
                                      key={`${field.id}-${option.id || optIdx}`}
                                      className="absolute"
                                      style={{
                                        left: `${circleLeftPercent}%`,
                                        top: `${circleTopPercent}%`,
                                        // NO transform - position is top-left corner, matching editor
                                        pointerEvents: 'none',
                                        zIndex: 100,
                                      }}
                                    >
                                      {field.type === 'radio' ? (
                                        // Radio circle ONLY
                                        <div
                                          style={{
                                            width: '16px',
                                            height: '16px',
                                            borderRadius: '50%',
                                            border: '2px solid #7C3AED',
                                            backgroundColor: 'white',
                                          }}
                                        />
                                      ) : (
                                        // Checkbox square ONLY
                                        <div
                                          style={{
                                            width: '16px',
                                            height: '16px',
                                            borderRadius: '2px',
                                            border: '2px solid #7C3AED',
                                            backgroundColor: 'white',
                                          }}
                                        />
                                      )}
                                      {/* NO LABELS - text is in document */}
                                    </div>
                                  );
                                })
                              ) : null}
                            </React.Fragment>
                          );
                        }
                        
                        // Default rendering for other field types
                        return (
                          <div
                            key={field.id || index}
                            className="absolute rounded"
                            style={{
                              left: `${leftPercent}%`,
                              top: `${topPercent}%`,
                              width: `${widthPercent}%`,
                              height: `${heightPercent}%`,
                              backgroundColor: getFieldColor(field.type),
                              border: '3px solid #7C3AED',
                              borderRadius: '4px',
                              pointerEvents: 'none',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '13px',
                              fontWeight: '700',
                              color: '#4C1D95',
                              zIndex: 100,
                              boxShadow: '0 2px 8px rgba(124, 58, 237, 0.3)'
                            }}
                          >
                            {getFieldLabel(field)}
                          </div>
                        );
                      })}
                    </div>
                  </Document>
                </div>
              </div>

              {/* Pagination Controls */}
              {numPages && (
                <div className="flex items-center justify-center gap-4 mt-4">
                  <Button
                    onClick={goToPrevPage}
                    disabled={pageNumber <= 1}
                    variant="outline"
                    size="sm"
                  >
                    <ChevronLeft size={16} />
                    Previous
                  </Button>
                  <span className="text-sm text-gray-600">
                    Page {pageNumber} of {numPages}
                  </span>
                  <Button
                    onClick={goToNextPage}
                    disabled={pageNumber >= numPages}
                    variant="outline"
                    size="sm"
                  >
                    Next
                    <ChevronRight size={16} />
                  </Button>
                </div>
              )}
            </div>

            {/* Fields List Sidebar */}
            <div className="bg-gray-50 rounded-lg p-4 overflow-auto">
              <h3 className="font-semibold text-gray-900 mb-4">
                Fields Overview ({fields.length})
              </h3>
              <div className="space-y-2">
                {fields.length === 0 ? (
                  <p className="text-sm text-gray-500">No fields placed yet</p>
                ) : (
                  fields.map((field, index) => (
                    <div
                      key={field.id || index}
                      className="p-3 bg-white rounded-lg border border-gray-200 hover:border-purple-300 transition-colors cursor-pointer"
                      onClick={() => setPageNumber(field.page)}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-gray-900">
                          {getFieldLabel(field)}
                        </span>
                        <span
                          className="text-xs px-2 py-1 rounded"
                          style={{ backgroundColor: getFieldColor(field.type) }}
                        >
                          {field.type}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500">
                        Page {field.page}
                        {field.required && (
                          <span className="ml-2 text-red-500">*Required</span>
                        )}
                      </div>
                      {field.recipientIndex !== undefined && (
                        <div className="text-xs text-purple-600 mt-1">
                          Recipient {field.recipientIndex + 1}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t p-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-600">
              {fields.length} field{fields.length !== 1 ? 's' : ''} placed on {numPages} page{numPages !== 1 ? 's' : ''}
            </p>
            <Button
              onClick={onClose}
              className="bg-gradient-to-r from-purple-600 via-pink-500 to-purple-600 hover:from-purple-700 hover:via-pink-600 hover:to-purple-700"
            >
              Close Preview
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default DocumentPreview;
